import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../Button2';
import styles from './AlertDialog.module.css';

/**
 * AlertDialog — a modal that interrupts the user with important content and
 * expects a response. shadcn/Radix composition, reimplemented in Athena's
 * idiom (TSX + CSS Module + 2.0 tokens). No Radix dependency: the open state
 * lives in a small context; Content portals to <body>, traps focus, closes on
 * Escape, locks body scroll, and — unlike a plain Dialog — does NOT close on
 * overlay click (the user must choose Cancel or an Action).
 *
 * Composition:
 *   AlertDialog
 *   ├── AlertDialogTrigger
 *   └── AlertDialogContent  (size: 'default' | 'sm')
 *       ├── AlertDialogHeader
 *       │   ├── AlertDialogMedia
 *       │   ├── AlertDialogTitle
 *       │   └── AlertDialogDescription
 *       └── AlertDialogFooter
 *           ├── AlertDialogCancel
 *           └── AlertDialogAction
 */

// Button2 is a JSX forwardRef primitive; cast for use from TSX.
const ButtonAny = Button as unknown as React.ComponentType<any>;

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const compose =
  (a?: (e: any) => void, b?: (e: any) => void) =>
  (e: any) => {
    a?.(e);
    if (!e.defaultPrevented) b?.(e);
  };

interface AlertDialogContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  titleId: string;
  descId: string;
}

const AlertDialogContext = createContext<AlertDialogContextValue | null>(null);

const useAlertDialog = (): AlertDialogContextValue => {
  const ctx = useContext(AlertDialogContext);
  if (!ctx) {
    throw new Error('AlertDialog subcomponents must be used within <AlertDialog>.');
  }
  return ctx;
};

/* ── Root ────────────────────────────────────────────────────────────── */

export interface AlertDialogProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: React.ReactNode;
}

const AlertDialog = ({ open: controlledOpen, defaultOpen = false, onOpenChange, children }: AlertDialogProps) => {
  const isControlled = controlledOpen !== undefined;
  const [internal, setInternal] = useState(defaultOpen);
  const open = isControlled ? controlledOpen : internal;
  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternal(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );
  const titleId = useId();
  const descId = useId();
  const ctx = useMemo(
    () => ({ open, setOpen, titleId, descId }),
    [open, setOpen, titleId, descId],
  );
  return <AlertDialogContext.Provider value={ctx}>{children}</AlertDialogContext.Provider>;
};

/* ── Trigger ─────────────────────────────────────────────────────────── */

export interface AlertDialogTriggerProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

const AlertDialogTrigger = ({ asChild = false, onClick, children, ...props }: AlertDialogTriggerProps) => {
  const { setOpen } = useAlertDialog();
  const open = (e: any) => {
    onClick?.(e);
    setOpen(true);
  };
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<any>, {
      onClick: compose((children as React.ReactElement<any>).props.onClick, open),
    });
  }
  return (
    <button type="button" data-slot="alert-dialog-trigger" onClick={open} {...props}>
      {children}
    </button>
  );
};

/* ── Content (+ overlay, portal, focus trap, escape, scroll lock) ─────── */

export interface AlertDialogContentProps
  extends React.HTMLAttributes<HTMLDivElement> {
  size?: 'default' | 'sm';
}

const AlertDialogContent = ({ size = 'default', className = '', children, ...props }: AlertDialogContentProps) => {
  const { open, setOpen, titleId, descId } = useAlertDialog();
  const contentRef = useRef<HTMLDivElement>(null);
  const prevFocus = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    prevFocus.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const t = window.setTimeout(() => {
      const root = contentRef.current;
      if (!root) return;
      const cancel = root.querySelector<HTMLElement>('[data-slot="alert-dialog-cancel"]');
      const first = root.querySelector<HTMLElement>(FOCUSABLE);
      (cancel ?? first ?? root).focus();
    }, 0);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);

    return () => {
      window.clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      (prevFocus.current as HTMLElement | null)?.focus?.();
    };
  }, [open, setOpen]);

  const onTrapKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !contentRef.current) return;
    const items = Array.from(contentRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!open) return null;

  return createPortal(
    <div className={styles.overlay}>
      <div
        ref={contentRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        data-slot="alert-dialog-content"
        className={`${styles.content} ${size === 'sm' ? styles.sm : ''} ${className}`.trim()}
        onKeyDown={onTrapKeyDown}
        {...props}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
};

/* ── Layout slots ────────────────────────────────────────────────────── */

const AlertDialogHeader = ({ className = '', ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div data-slot="alert-dialog-header" className={`${styles.header} ${className}`.trim()} {...props} />
);

const AlertDialogMedia = ({ className = '', ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div data-slot="alert-dialog-media" className={`${styles.media} ${className}`.trim()} {...props} />
);

const AlertDialogTitle = ({ className = '', ...props }: React.HTMLAttributes<HTMLHeadingElement>) => {
  const { titleId } = useAlertDialog();
  return <h2 id={titleId} data-slot="alert-dialog-title" className={`${styles.title} ${className}`.trim()} {...props} />;
};

const AlertDialogDescription = ({ className = '', ...props }: React.HTMLAttributes<HTMLParagraphElement>) => {
  const { descId } = useAlertDialog();
  return <p id={descId} data-slot="alert-dialog-description" className={`${styles.description} ${className}`.trim()} {...props} />;
};

const AlertDialogFooter = ({ className = '', ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div data-slot="alert-dialog-footer" className={`${styles.footer} ${className}`.trim()} {...props} />
);

/* ── Action buttons (close on click; Action takes a Button2 variant) ──── */

export interface AlertDialogCancelProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

const AlertDialogCancel = ({ asChild = false, onClick, children, ...props }: AlertDialogCancelProps) => {
  const { setOpen } = useAlertDialog();
  const close = (e: any) => {
    onClick?.(e);
    setOpen(false);
  };
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<any>, {
      'data-slot': 'alert-dialog-cancel',
      onClick: compose((children as React.ReactElement<any>).props.onClick, close),
    });
  }
  return (
    <ButtonAny variant="outline" data-slot="alert-dialog-cancel" onClick={close} {...props}>
      {children}
    </ButtonAny>
  );
};

export interface AlertDialogActionProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  /** Button2 variant — pass 'destructive' for a destructive action. */
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
}

const AlertDialogAction = ({ asChild = false, variant = 'default', onClick, children, ...props }: AlertDialogActionProps) => {
  const { setOpen } = useAlertDialog();
  const act = (e: any) => {
    onClick?.(e);
    setOpen(false);
  };
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<any>, {
      'data-slot': 'alert-dialog-action',
      onClick: compose((children as React.ReactElement<any>).props.onClick, act),
    });
  }
  return (
    <ButtonAny variant={variant} data-slot="alert-dialog-action" onClick={act} {...props}>
      {children}
    </ButtonAny>
  );
};

export default AlertDialog;
export {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
};
